import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Stack,
  Divider
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import BackToDashboard from "../common/BackToAdminDashboard";

const LANGS = [
  { key: "en", label: "English (en) *" },
  { key: "el", label: "Greek (el) *" },
  { key: "en_gb", label: "English UK (en_gb)" },
  { key: "en_us", label: "English US (en_us)" },
  { key: "es", label: "Spanish (es)" },
  { key: "fr", label: "French (fr)" },
  { key: "de", label: "German (de)" },
  { key: "it", label: "Italian (it)" },
  { key: "ro", label: "Romanian (ro)" },
  { key: "ru", label: "Russian (ru)" },
  { key: "sr", label: "Serbian (sr)" },
  { key: "sq", label: "Albanian (sq)" },
  { key: "bg", label: "Bulgarian (bg)" },
  { key: "hr", label: "Croatian (hr)" },
  { key: "sv", label: "Swedish (sv)" },
  { key: "ar", label: "Arabic (ar)" },
  { key: "ur", label: "Urdu (ur)" },
  { key: "zh", label: "Chinese (zh)" },
];

const emptyNames = () => Object.fromEntries(LANGS.map((l) => [l.key, ""]));

export default function UpdateGenericProduct() {
  const navigate = useNavigate();

  const [allGPs, setAllGPs] = useState([]);
  const [selectedGP, setSelectedGP] = useState(null);

  const [names, setNames] = useState(() => emptyNames());

  const [allProducts, setAllProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [error, setError] = useState(null);

  useEffect(() => {
    api.genericProducts
      .list()
      .then((resp) => {
        // backend: { success: true, data: [...] }
        const items = resp.data?.data || resp.data || [];
        setAllGPs(Array.isArray(items) ? items : []);
      })
      .catch(() => setError("Failed to load generic products"));
  }, []);

  useEffect(() => {
    api.products
      .list()
      .then((resp) => setAllProducts(resp.data || []))
      .catch(() => setError("Failed to load products"));
  }, []);

  const canSubmit = useMemo(() => {
    return selectedGP && (names.en || "").trim() && (names.el || "").trim();
  }, [selectedGP, names]);

  const handleSelectGP = (gpId) => {
    const gp = allGPs.find((g) => g.id === gpId);
    if (!gp) return;

    setSelectedGP(gp);

    // gp.names είναι αυτό που δίνει το backend _serialize_gp
    const nextNames = { ...emptyNames(), ...(gp.names || {}) };
    setNames(nextNames);

    // linked products: όσα έχουν generic_product_id == gp.id
    const linked = allProducts.filter((p) => p.generic_product_id === gp.id);
    setSelectedProducts(linked);
  };

  const handleNameChange = (langKey) => (e) => {
    const v = e.target.value;
    setNames((prev) => ({ ...prev, [langKey]: v }));
  };

  const fillEmptyFrom = (sourceKey) => {
    const src = (names[sourceKey] || "").trim();
    if (!src) return;
    setNames((prev) => {
      const next = { ...prev };
      for (const { key } of LANGS) {
        if (!next[key] || !next[key].trim()) next[key] = src;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedGP) {
      setError("Please select a generic product first.");
      return;
    }

    if (!canSubmit) {
      setError("Please fill at least English (en) and Greek (el).");
      return;
    }

    try {
      const productIds = selectedProducts.map((p) => p.id);

      await api.genericProducts.update(selectedGP.id, {
        names,
        product_ids: productIds,
      });

      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update generic product");
    }
  };

  const getProductLabel = (p) => p.name_el || p.name_en || `Untitled (ID: ${p.id})`;
  const gpLabel = (gp) => gp.names?.el || gp.names?.en || gp.name || `#${gp.id}`;

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <BackToDashboard dashboardPath="/admin/dashboard" />

      <Typography variant="h4" gutterBottom>
        Update Generic Product (All Languages)
      </Typography>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel id="gp-select-label">Select Generic Product</InputLabel>
        <Select
          labelId="gp-select-label"
          value={selectedGP ? selectedGP.id : ""}
          label="Select Generic Product"
          onChange={(e) => handleSelectGP(e.target.value)}
        >
          {allGPs.map((gp) => (
            <MenuItem key={gp.id} value={gp.id}>
              {gpLabel(gp)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedGP && (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
            <Button variant="outlined" onClick={() => fillEmptyFrom("en")}>
              Fill empty from EN
            </Button>
            <Button variant="outlined" onClick={() => fillEmptyFrom("el")}>
              Fill empty from EL
            </Button>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            {LANGS.map((l) => (
              <TextField
                key={l.key}
                label={l.label}
                value={names[l.key]}
                onChange={handleNameChange(l.key)}
                required={l.key === "en" || l.key === "el"}
                fullWidth
              />
            ))}
          </Box>

          <Autocomplete
            multiple
            options={allProducts}
            value={selectedProducts}
            onChange={(_, newValue) => setSelectedProducts(newValue)}
            getOptionLabel={getProductLabel}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {getProductLabel(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Re-link Products (optional)" placeholder="Type or scroll..." />
            )}
            sx={{ mt: 3 }}
          />

          <Button variant="contained" color="primary" type="submit" sx={{ mt: 3 }} disabled={!canSubmit}>
            Save Changes
          </Button>
        </Box>
      )}
    </Container>
  );
}
